<template>
  <div class="ai-script-flow">
    <div class="ai-script-flow__toolbar">
      <div class="ai-script-flow__toolbar-copy">
        <span class="ai-script-flow__title">Call script flow</span>
        <p class="ai-script-flow__hint mb-0">
          Teal chips are job-detail values. Hear a step before you call anyone.
        </p>
      </div>
    </div>

    <div v-if="!steps.length" class="ai-script-flow__empty">
      <feather-icon icon="FileTextIcon" size="22" class="ai-script-flow__empty-icon" />
      <p class="ai-script-flow__empty-title">No script yet</p>
      <p class="mb-0">The call flow will appear here once a call type is selected.</p>
    </div>

    <div v-else class="ai-script-flow__timeline">
      <div
        v-for="(step, idx) in steps"
        :key="step.id"
        class="ai-script-flow__step"
        :class="{ 'ai-script-flow__step--open': isStepOpen(step.id) }"
      >
        <div class="ai-script-flow__marker">
          <span class="ai-script-flow__dot" :class="`ai-script-flow__dot--${step.kind}`" />
          <span v-if="idx < steps.length - 1" class="ai-script-flow__line" />
        </div>
        <div class="ai-script-flow__card">
          <div class="ai-script-flow__card-header">
            <button
              type="button"
              class="ai-script-flow__step-toggle"
              :aria-expanded="isStepOpen(step.id) ? 'true' : 'false'"
              @click="toggleStep(step.id)"
            >
              <feather-icon
                :icon="isStepOpen(step.id) ? 'ChevronDownIcon' : 'ChevronRightIcon'"
                size="14"
                class="ai-script-flow__chevron"
              />
              <span class="ai-script-flow__step-num">{{ idx + 1 }}</span>
              <ai-calling-token-text
                class="ai-script-flow__step-label"
                :text="step.label"
                :values="variableValues"
                resolve
              />
            </button>
            <b-button
              variant="outline-primary"
              class="ai-script-flow__demo-btn"
              :disabled="disabled || previewingStepId === step.id"
              :aria-label="`Hear demo of ${step.label}`"
              @click.stop="$emit('demo-step', step)"
            >
              <b-spinner v-if="previewingStepId === step.id" small class="mr-25" />
              <feather-icon v-else icon="Volume2Icon" size="15" class="mr-25" />
              {{ previewingStepId === step.id ? 'Playing' : 'Demo' }}
            </b-button>
          </div>

          <p
            v-if="!isStepOpen(step.id) && step.spokenText"
            class="ai-script-flow__teaser mb-0"
          >
            <ai-calling-token-text
              :text="step.templateText || step.spokenText"
              :values="variableValues"
              resolve
            />
          </p>

          <div v-else-if="isStepOpen(step.id)">
            <div v-if="editable" class="ai-script-flow__editor">
              <label class="ai-script-flow__editor-label" :for="'ai-step-' + step.id">
                Edit this line
              </label>
              <b-form-textarea
                :id="'ai-step-' + step.id"
                :value="step.templateText"
                class="ai-script-flow__textarea"
                :rows="stepRows(step.templateText)"
                max-rows="8"
                :disabled="disabled"
                spellcheck="false"
                autocomplete="off"
                autocorrect="off"
                autocapitalize="off"
                lang="und"
                @input="onStepInput(step, $event)"
              />
              <div v-if="step.spokenText" class="ai-script-flow__preview">
                <span class="ai-script-flow__preview-label">Candidate hears</span>
                <p class="ai-script-flow__preview-text mb-0">
                  <ai-calling-token-text
                    :text="step.templateText"
                    :values="variableValues"
                    resolve
                  />
                </p>
              </div>
            </div>
            <div v-else class="ai-script-flow__readonly">
              <ai-calling-token-text
                :text="step.templateText || step.spokenText"
                :values="variableValues"
                resolve
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { BButton, BSpinner, BFormTextarea } from 'bootstrap-vue'
import AiCallingTokenText from '@/views/apps/jobs/jobs-view/components/AiCallingTokenText.vue'

export default {
  name: 'AiCallingScriptFlow',
  components: { BButton, BSpinner, BFormTextarea, AiCallingTokenText },
  props: {
    steps: { type: Array, default: () => [] },
    disabled: { type: Boolean, default: false },
    previewingStepId: { type: String, default: '' },
    editable: { type: Boolean, default: false },
    variableValues: { type: Object, default: () => ({}) },
  },
  data() {
    return {
      openStepId: '',
    }
  },
  watch: {
    steps: {
      immediate: true,
      handler(steps) {
        if (!steps || !steps.length) {
          this.openStepId = ''
          return
        }
        const ids = steps.map(step => step.id)
        if (!ids.includes(this.openStepId)) {
          this.openStepId = steps[0].id
        }
        this.$nextTick(this.disableSpellcheck)
      },
    },
  },
  mounted() {
    this.disableSpellcheck()
  },
  updated() {
    this.disableSpellcheck()
  },
  methods: {
    disableSpellcheck() {
      const root = this.$el
      if (!root || !root.querySelectorAll) return
      root.querySelectorAll('textarea').forEach(el => {
        el.setAttribute('spellcheck', 'false')
        el.spellcheck = false
      })
    },
    isStepOpen(id) {
      return this.openStepId === id
    },
    toggleStep(id) {
      this.openStepId = this.openStepId === id ? '' : id
    },
    onStepInput(step, value) {
      this.$emit('update-step', { id: step.id, text: value })
    },
    stepRows(text) {
      const src = String(text || '')
      const lines = src.split('\n').length
      const approx = Math.ceil(src.length / 68)
      return Math.min(8, Math.max(3, lines, approx))
    },
  },
}
</script>

<style scoped>
.ai-script-flow__toolbar {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.5rem 0.75rem;
  padding-bottom: 0.75rem;
  margin-bottom: 0.25rem;
  border-bottom: 1px solid rgba(47, 43, 61, 0.1);
}
.ai-script-flow__title {
  display: block;
  font-size: 16px;
  font-weight: 700;
  color: #2f2b3d;
  line-height: 1.3;
}
.ai-script-flow__hint {
  margin-top: 0.15rem;
  font-size: 13px;
  line-height: 1.4;
  color: #6e6b7b;
}
.ai-script-flow__count {
  font-size: 12px;
  font-weight: 700;
  padding: 0.3rem 0.55rem;
  flex-shrink: 0;
}
.ai-script-flow__empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 1.5rem 1rem;
  border: 1px dashed rgba(47, 43, 61, 0.16);
  border-radius: 0.5rem;
  background: #f8f8f8;
  color: #6e6b7b;
  font-size: 14px;
}
.ai-script-flow__empty-icon {
  color: #00ac99;
  margin-bottom: 0.4rem;
}
.ai-script-flow__empty-title {
  font-size: 15px;
  font-weight: 600;
  color: #2f2b3d;
  margin-bottom: 0.25rem;
}
.ai-script-flow__timeline {
  display: flex;
  flex-direction: column;
  padding-top: 0.35rem;
}
.ai-script-flow__step {
  display: flex;
  align-items: stretch;
  gap: 0.55rem;
}
.ai-script-flow__marker {
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 0.75rem;
  flex-shrink: 0;
  padding-top: 0.85rem;
}
.ai-script-flow__dot {
  width: 0.5rem;
  height: 0.5rem;
  border-radius: 50%;
  background: #00ac99;
  flex-shrink: 0;
}
.ai-script-flow__dot--greeting {
  background: #28c76f;
}
.ai-script-flow__dot--closing {
  background: #ff9f43;
}
.ai-script-flow__line {
  flex: 1;
  width: 2px;
  min-height: 0.75rem;
  background: #ebe9f1;
  margin: 0.2rem 0;
}
.ai-script-flow__card {
  flex: 1;
  min-width: 0;
  margin-bottom: 0.55rem;
  padding: 0.7rem 0.85rem;
  border: 1px solid rgba(47, 43, 61, 0.12);
  border-radius: 0.5rem;
  background: #fff;
}
.ai-script-flow__step--open .ai-script-flow__card {
  border-color: rgba(0, 172, 153, 0.35);
  box-shadow: 0 0 0 1px rgba(0, 172, 153, 0.08);
}
.ai-script-flow__card-header {
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  align-items: center;
  gap: 0.45rem 0.65rem;
}
.ai-script-flow__step-toggle {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  min-width: 0;
  flex: 1 1 12rem;
  margin: 0;
  padding: 0;
  border: 0;
  background: transparent;
  text-align: left;
  cursor: pointer;
  color: inherit;
}
.ai-script-flow__step-toggle:focus {
  outline: none;
}
.ai-script-flow__step-toggle:focus-visible {
  outline: 2px solid #00ac99;
  outline-offset: 2px;
  border-radius: 0.25rem;
}
.ai-script-flow__chevron {
  color: #6e6b7b;
  flex-shrink: 0;
}
.ai-script-flow__step-num {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.35rem;
  height: 1.35rem;
  border-radius: 999px;
  background: rgba(0, 172, 153, 0.12);
  color: #007a6e;
  font-size: 11px;
  font-weight: 700;
  flex-shrink: 0;
}
.ai-script-flow__step-label {
  font-size: 15px;
  font-weight: 700;
  color: #2f2b3d;
  line-height: 1.35;
}
.ai-script-flow__teaser {
  margin-top: 0.4rem;
  font-size: 14px;
  line-height: 1.45;
  color: #6e6b7b;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
.ai-script-flow__demo-btn {
  flex-shrink: 0;
  font-size: 13px;
  font-weight: 700;
  min-height: 2.15rem;
  min-width: 5.75rem;
  padding: 0.3rem 0.75rem;
  background: rgba(0, 172, 153, 0.1);
  border-color: #00ac99;
  color: #007a6e;
  box-shadow: none;
}
.ai-script-flow__demo-btn:hover:not(:disabled),
.ai-script-flow__demo-btn:focus:not(:disabled) {
  background: rgba(0, 172, 153, 0.18);
  border-color: #009886;
  color: #00665c;
  box-shadow: none;
}
.ai-script-flow__editor {
  margin-top: 0.55rem;
}
.ai-script-flow__editor-label {
  display: block;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.01em;
  color: #6e6b7b;
  margin-bottom: 0.3rem;
}
.ai-script-flow__textarea {
  font-size: 15px;
  line-height: 1.55;
  color: #2f2b3d;
  background: #fff;
  border-color: rgba(47, 43, 61, 0.18);
  resize: vertical;
  min-height: 4.5rem;
}
.ai-script-flow__textarea:disabled {
  background: #f8f8f8;
}
.ai-script-flow__preview {
  margin-top: 0.55rem;
  padding: 0.55rem 0.7rem;
  border-radius: 0.4rem;
  background: rgba(0, 172, 153, 0.06);
  border: 1px solid rgba(0, 172, 153, 0.18);
}
.ai-script-flow__preview-label {
  display: block;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #007a6e;
  margin-bottom: 0.25rem;
}
.ai-script-flow__preview-text {
  font-size: 15px;
  line-height: 1.55;
  color: #2f2b3d;
}
.ai-script-flow__readonly {
  margin-top: 0.45rem;
  font-size: 15px;
  line-height: 1.55;
  color: #4b465c;
}

@media (max-width: 575.98px) {
  .ai-script-flow__demo-btn {
    width: 100%;
  }
  .ai-script-flow__card-header {
    align-items: stretch;
  }
}
</style>
